import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Analytics from '../utils/analytics';

const CustomerDashboard = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [error, setError] = useState('');
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/login?redirect=/dashboard');
            return;
        }

        if (isAuthenticated) {
            fetchBookings();
        }
    }, [isAuthenticated, authLoading]);

    const fetchBookings = async () => {
        try {
            const data = await api.get('/api/bookings?role=customer').then(res => res.json());
            setBookings(data);
        } catch (err) {
            console.error("Failed to fetch bookings", err);
            setError('Failed to load bookings.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelClick = (booking) => {
        setSelectedBooking(booking);
        setCancelModalOpen(true);
    };

    const confirmCancel = async () => {
        if (!selectedBooking) return;
        try {
            const res = await api.patch(`/api/customer/bookings/${selectedBooking.bookingId}/cancel`, {});
            if (res.ok) {
                // Update local state
                setBookings(prev => prev.map(b =>
                    b.bookingId === selectedBooking.bookingId
                        ? { ...b, status: 'CANCELLED' }
                        : b
                ));
                setCancelModalOpen(false);
                setSelectedBooking(null);

                // Analytics: Track cancellation
                const hoursBeforeAppt = Math.floor((new Date(selectedBooking.startTime) - new Date()) / (1000 * 60 * 60));
                Analytics.pushCustomEvent('booking_cancelled_by_customer', {
                    hours_before_appointment: hoursBeforeAppt
                });
            } else {
                const text = await res.text();
                alert("Failed to cancel: " + text);
            }
        } catch (err) {
            console.error(err);
            alert("Error cancelling booking");
        }
    };

    // Filter and sort bookings
    const now = new Date();
    const upcoming = bookings.filter(b => {
        const start = new Date(b.startTime);
        return start >= now && b.status !== 'CANCELLED' && b.status !== 'COMPLETED';
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const past = bookings.filter(b => {
        const start = new Date(b.startTime);
        return start < now || b.status === 'CANCELLED' || b.status === 'COMPLETED';
    }).sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

    if (authLoading || loading) return <div className="loading-spinner">Loading...</div>;

    return (
        <div className="customer-dashboard-container">
            <div className="dashboard-header">
                <h1>My Dashboard</h1>
                <p>Welcome back, {user?.name}</p>
            </div>

            <div className="dashboard-tabs">
                <button
                    className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upcoming')}
                >
                    Upcoming ({upcoming.length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
                    onClick={() => setActiveTab('past')}
                >
                    Past & Cancelled
                </button>
            </div>

            <div className="bookings-list">
                {activeTab === 'upcoming' ? (
                    upcoming.length === 0 ? (
                        <div className="empty-state">
                            <p>You have no upcoming appointments.</p>
                            <button className="btn-primary" onClick={() => navigate('/book')}>Book a Service</button>
                        </div>
                    ) : (
                        upcoming.map(booking => (
                            <BookingCard
                                key={booking.bookingId}
                                booking={booking}
                                onCancel={() => handleCancelClick(booking)}
                            />
                        ))
                    )
                ) : (
                    past.length === 0 ? (
                        <div className="empty-state"><p>No past history.</p></div>
                    ) : (
                        past.map(booking => (
                            <BookingCard key={booking.bookingId} booking={booking} isPast={true} />
                        ))
                    )
                )}
            </div>

            {cancelModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Cancel Appointment?</h3>
                        <p>Are you sure you want to cancel your appointment with <strong>{selectedBooking?.artisanName}</strong>?</p>
                        <p className="warning-text">This will release the slot for other customers.</p>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setCancelModalOpen(false)}>Keep it</button>
                            <button className="btn-danger" onClick={confirmCancel}>Yes, Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const BookingCard = ({ booking, onCancel, isPast }) => {
    const date = new Date(booking.startTime);
    const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'CONFIRMED': return <span className="status-badge success">Confirmed</span>;
            case 'PENDING_APPROVAL': return <span className="status-badge warning">Pending</span>;
            case 'CANCELLED': return <span className="status-badge error">Cancelled</span>;
            case 'COMPLETED': return <span className="status-badge success">Completed</span>;
            default: return <span className="status-badge">{status}</span>;
        }
    };

    return (
        <div className="booking-card">
            <div className="card-header">
                {getStatusBadge(booking.status)}
                {/* <span className="price">${(booking.finalPriceCents / 100).toFixed(2)}</span> */}
            </div>
            <div className="card-body">
                <h3>{booking.serviceName}</h3>
                <p className="booking-time">{dateStr} at {timeStr}</p>
                <div className="artisan-info">
                    {/* Placeholder Avatar */}
                    <div className="avatar-circle">{booking.artisanName?.charAt(0)}</div>
                    <span>{booking.artisanName}</span>
                </div>
                <p className="payment-info">
                    Amount: Not implemented yet (To be paid on-site)
                </p>
            </div>
            {!isPast && (
                <div className="card-actions">
                    <button className="btn-outline-danger full-width" onClick={onCancel}>Cancel Appointment</button>
                </div>
            )}
        </div>
    );
};

export default CustomerDashboard;
